import { isRjBirdTenantProfile } from '../services/rj-bird-rate-card-sync';
import { isDemoBusiness } from '../services/demo-business';
import { normalizeStrataPlanNumber } from './strata-plan';
import { STRATA_MANAGER_DOMAINS } from '../config/strata-manager-domains';

/** The addressee a business with strata_quotes_to_owners on prints on a strata quote. */
export const OWNERS_ADDRESSEE = 'The Owners of Strata Plan';

/**
 * Returns true if the active tenant profile is RJ Bird Building Pty Ltd.
 * When the tenant profile is null, loading, or unseeded, defaults to true
 * because QuoteMaker's default Customer Zero environment is RJ Bird.
 * Returns false when an explicit non-RJ Bird tenant profile is active, and
 * always false for a demo business, even before its profile has loaded
 * (services/demo-business.ts), so a demo never borrows RJ Bird's marks.
 *
 * Only the letterhead marks read this now (src/utils/quote-letterhead.ts). The
 * strata addressee is a stored setting read by addressesStrataQuotesToOwners(),
 * which treats an unknown profile as NOT RJ Bird.
 *
 * @param {object|null} tenantProfile
 * @returns {boolean}
 */
export function isRjBirdTenant(tenantProfile) {
    if (isDemoBusiness()) return false;
    if (!tenantProfile || typeof tenantProfile !== 'object') return true;
    if (!tenantProfile.legalName && !tenantProfile.tradingName) return true;
    return isRjBirdTenantProfile(tenantProfile);
}

/**
 * Whether this business addresses a strata quote to "The Owners of Strata Plan"
 * instead of the client company entered in Stage 1. The operator sets it per
 * business in the Control Panel (tenant_profiles.strata_quotes_to_owners,
 * migration 127). RJ Bird has it on; every other business starts with it off.
 *
 * An unknown profile (not loaded yet, a failed read, no profile row) answers
 * false: the quote keeps the client company the operator entered, which they
 * see and can change in Stage 5. Answering true would give another business
 * RJ Bird's addressee without anyone noticing.
 *
 * A profile read from a backend that predates migration 127 carries no value.
 * RJ Bird is then recognised by name, so its quotes do not change while the
 * frontend and backend are deployed at different times.
 *
 * @param {object|null} tenantProfile
 * @returns {boolean}
 */
export function addressesStrataQuotesToOwners(tenantProfile) {
    if (!tenantProfile || typeof tenantProfile !== 'object') return false;
    if (typeof tenantProfile.strataQuotesToOwners === 'boolean') return tenantProfile.strataQuotesToOwners;
    return isRjBirdTenantProfile(tenantProfile);
}

/**
 * Resolves the default client company name for a quote.
 *
 * On a strata job, a business with addressesStrataQuotesToOwners() on gets
 * 'The Owners of Strata Plan' instead of the managing agent's name extracted
 * into formData.clientCompany. Every other case gets formData.clientCompany,
 * unless tenantProfile.strataClientCompanyDefault names another addressee.
 *
 * @param {{
 *   tenantProfile?: object|null,
 *   formData?: object|null,
 *   strataPlan?: string|null,
 *   isStrata?: boolean,
 * }} options
 * @returns {string}
 */
export function resolveDefaultClientCompany({
    tenantProfile = null,
    formData = null,
    strataPlan = null,
    isStrata = false,
} = {}) {
    const hasStrata = isStrataJob({ strataPlan, isStrata, formData });

    // Allow explicit override configured on tenant profile if present
    if (tenantProfile?.strataClientCompanyDefault && hasStrata) {
        return String(tenantProfile.strataClientCompanyDefault).trim();
    }

    if (hasStrata && addressesStrataQuotesToOwners(tenantProfile)) {
        return OWNERS_ADDRESSEE;
    }

    const clientCompany = String(formData?.clientCompany || '').trim();
    // An older Stage 5 save copied the generated owners addressee into
    // clientCompany. On a job that is not strata it names nobody (D7), so the
    // firm that sent the request stands in for it.
    if (!hasStrata && isGeneratedOwnersAddressee(clientCompany, tenantProfile)) {
        const requesterCompany = String(formData?.requesterCompany || '').trim();
        return requesterCompany && !isOwnersAddresseeWording(requesterCompany, tenantProfile) ? requesterCompany : '';
    }
    return clientCompany;
}

/**
 * True when the text is the owners addressee exactly as QuoteMaker generates
 * it ("The Owners of Strata Plan", or the business's configured strata
 * addressee) for a business that addresses strata quotes to the owners.
 *
 * Before lane A3 the quote author also wrote it on jobs that are not strata,
 * whenever the project reference held a street address (F5), and an older
 * Stage 5 save copied it into formData.clientCompany. On a job that is not
 * strata, such text is that bug's leftover, never an addressee anyone chose.
 * Owners wording an operator typed in another form ("The Owners - Strata Plan
 * 19352") does not match. A business without the setting never matches (I5).
 *
 * @param {unknown} value
 * @param {object|null} [tenantProfile]
 * @returns {boolean}
 */
export function isGeneratedOwnersAddressee(value, tenantProfile = null) {
    const text = String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
    if (!text) return false;
    const configured = String(tenantProfile?.strataClientCompanyDefault || '').trim().replace(/\s+/g, ' ').toLowerCase();
    if (configured && text === configured) return true;
    return addressesStrataQuotesToOwners(tenantProfile) && text === OWNERS_ADDRESSEE.toLowerCase();
}

/**
 * A strata job is one with a strata plan number the shared parser reads
 * (utils/strata-plan.ts), or an explicit strata flag. Any other text is not:
 * "N/A" or "Lot 5 DP 1234" in the strata field, or a street address in the
 * quote's project reference (the AI writes "<address | strata plan>" there),
 * used to make an ordinary job print "The Owners of Strata Plan".
 *
 * @param {{ strataPlan?: unknown, isStrata?: unknown, formData?: object|null }} options
 * @returns {boolean}
 */
export function isStrataJob({ strataPlan = null, isStrata = false, formData = null } = {}) {
    return Boolean(
        normalizeStrataPlanNumber(strataPlan)
        || isStrata === true
        || normalizeStrataPlanNumber(formData?.strataplan)
        || normalizeStrataPlanNumber(formData?.strataPlan)
        || formData?.isStrata === true
        || formData?.clientType === 'strata'
    );
}

/**
 * Whether the owners addressee is in force for this job: the business has
 * strata_quotes_to_owners on and the job is a strata job. Only then does the
 * printed quote keep the managing agent's name out (DEC-COUNCIL-018, D7).
 */
export function strataOwnersAddresseeApplies({ tenantProfile = null, formData = null, strataPlan = null } = {}) {
    return addressesStrataQuotesToOwners(tenantProfile) && isStrataJob({ strataPlan, formData });
}

// Wording that names the owners of a building, never the firm that manages it.
const OWNERS_WORDING_PATTERNS = [
    /^\s*the\s+owners\b/i,
    /\bstrata\s*(?:plan|scheme)\b/i,
    /^\s*s\.?\s?p\.?\s*[:#]?\s*\d{2,7}\b/i,
];

// "Owners Corporation" and "Body Corporate" also appear in firm names
// ("Whittles Body Corporate", "Body Corporate Services Pty Ltd"). They name
// the owners only at the start of the text or before the scheme they belong
// to ("Body Corporate for Riverside CTS 1234", "Riverside Body Corporate CTS
// 1234"), and never in a name that ends the way a firm's name does.
const CORPORATE_OWNERS_PHRASE = /\b(?:owners?\s+corp(?:oration)?|body\s+corp(?:orate)?)\b\.?/i;
const OWNERS_SCHEME_FOLLOWS = /^\s*(?:[-:(#]|\d|(?:for|of|no|number|plan|scheme|cts|community|sp|ps)\b)/i;
const FIRM_NAME_ENDING = /\b(?:services|management|managers?|group|solutions|partners|administration|australia|network|consulting|consultants?|realty|property|properties|strata|pty\s+ltd|pty\s+limited|proprietary\s+limited|limited|ltd|inc|p\s*\/?\s*l)\.?\s*$/i;

function namesOwnersCorporation(text) {
    const found = text.match(CORPORATE_OWNERS_PHRASE);
    if (!found) return false;
    if (FIRM_NAME_ENDING.test(text)) return false;
    const before = text.slice(0, found.index).trim();
    if (before === '' || /^the$/i.test(before)) return true;
    return OWNERS_SCHEME_FOLLOWS.test(text.slice(found.index + found[0].length));
}

/**
 * True when the text is an owners-corporation addressee ("The Owners of
 * Strata Plan", "The Owners - Strata Plan 19180", "Owners Corporation SP 1234",
 * "Body Corp (Strata Plan No. 5517)") or the business's own configured strata
 * addressee. Such text is the printed addressee, never the requesting firm, so
 * it must not be written into formData.clientCompany (D6).
 *
 * @param {unknown} value
 * @param {object|null} [tenantProfile]
 * @returns {boolean}
 */
export function isOwnersAddresseeWording(value, tenantProfile = null) {
    const text = String(value ?? '').trim();
    if (!text) return false;
    const configured = String(tenantProfile?.strataClientCompanyDefault || '').trim();
    if (configured && text.toLowerCase() === configured.toLowerCase()) return true;
    return OWNERS_WORDING_PATTERNS.some(pattern => pattern.test(text)) || namesOwnersCorporation(text);
}

const FIRM_LEGAL_SUFFIX = /\s+(?:pty\s+ltd|pty\s+limited|proprietary\s+limited|limited|ltd|inc|p\s+l)$/;

function words(value) {
    return String(value ?? '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}

/** A firm name reduced for comparison: case, punctuation and "Pty Ltd" removed. */
function firmKey(value) {
    return words(value).replace(FIRM_LEGAL_SUFFIX, '').trim();
}

/** True when two names are the same firm ("Strata Partners" and "STRATA PARTNERS PTY LTD"). */
export function sameFirmName(a, b) {
    const left = firmKey(a);
    return !!left && left === firmKey(b);
}

const KNOWN_STRATA_MANAGER_NAMES = Object.values(STRATA_MANAGER_DOMAINS).map(entry => entry.canonicalName);

/**
 * True when a name reads as a company, not a person: it ends in a legal
 * suffix ("Pty Ltd"), names strata, a body corporate or management, or is a
 * known strata manager.
 */
function readsAsCompany(name) {
    const text = words(name);
    if (!text) return false;
    if (FIRM_LEGAL_SUFFIX.test(text)) return true;
    if (/\b(?:strata|body corporate|management)\b/.test(text)) return true;
    return KNOWN_STRATA_MANAGER_NAMES.some(known => sameFirmName(known, name));
}

/**
 * The names of the firm that sent a strata request, as the job knows them:
 * the CRM firm it is linked to, the requester company and the Quote Request's
 * client company. Owners wording is left out (an older Stage 5 save wrote it
 * into clientCompany), and names of the same firm appear once.
 *
 * A name that is the requester's or the site contact's own name is that
 * person, not the managing firm, unless it reads as a company. A lot owner's
 * job can be linked to a CRM client named after him, or carry his name as the
 * client company. Counting that name as the firm dropped the person from the
 * Attention line and the email greeting.
 *
 * @param {{ firmNames?: unknown, formData?: object|null, tenantProfile?: object|null }} options
 * @returns {string[]}
 */
export function managingFirmNames({ firmNames = [], formData = null, tenantProfile = null } = {}) {
    const names = [];
    const known = Array.isArray(firmNames) ? firmNames : [firmNames];
    const people = [formData?.clientName, formData?.siteContactName];
    for (const raw of [...known, formData?.requesterCompany, formData?.clientCompany]) {
        const name = String(raw ?? '').trim();
        if (!name || isOwnersAddresseeWording(name, tenantProfile)) continue;
        if (!readsAsCompany(name) && people.some(person => sameFirmName(person, name))) continue;
        if (names.some(existing => sameFirmName(existing, name))) continue;
        names.push(name);
    }
    return names;
}

/**
 * True when the text names the firm as whole words. A name shorter than four
 * characters once reduced is too likely to match ordinary words.
 */
export function textNamesFirm(text, firmName) {
    const needle = firmKey(firmName);
    if (needle.length < 4) return false;
    return ` ${words(text)} `.includes(` ${needle} `);
}

/**
 * The Attention line of a strata quote addressed to the owners: the first
 * candidate that is not the managing firm's name, so "Attention: Strata
 * Partners" falls back to the person who asked. '' when every candidate names
 * the firm.
 *
 * @param {{ candidates?: unknown[], firmNames?: string[] }} options
 * @returns {string}
 */
export function resolvePrintedAttention({ candidates = [], firmNames = [] } = {}) {
    for (const raw of candidates) {
        const value = String(raw ?? '').trim();
        if (!value) continue;
        if (firmNames.some(firm => sameFirmName(firm, value))) continue;
        return value;
    }
    return '';
}

/**
 * True when the operator deliberately emptied the Attention line: client.attentionEditedByOperator is set, the field is present
 * and blank. Only then is a blank Attention a decision. A quote with no Attention field, or a generated blank nobody edited, still
 * takes the requester's name (resolvePrintedAttention above, the Stage 5 seed, generation's fill). Mirrors
 * client.companyEditedByOperator.
 *
 * @param {{ attention?: unknown, attentionEditedByOperator?: unknown }|null|undefined} client
 * @returns {boolean}
 */
export function isAttentionClearedByOperator(client) {
    return client?.attentionEditedByOperator === true
        && Object.prototype.hasOwnProperty.call(client, 'attention')
        && String(client.attention ?? '').trim() === '';
}

/**
 * The Attention name the operator wrote, exactly as they wrote it: client.attentionEditedByOperator is set and the line holds text.
 * '' for anything else (no mark, a blank, a cleared line: a clear is isAttentionClearedByOperator's, not a name). It is the
 * operator's own text, so it is returned untouched: no trimming, no person-name parsing, no RFQ evidence (resolveQuoteAttention
 * extracts a person from RFQ text and would replace or reject what was typed). A provider-failure fallback uses it to keep the
 * recipient (C30).
 *
 * @param {{ attention?: unknown, attentionEditedByOperator?: unknown }|null|undefined} client
 * @returns {string}
 */
export function authoredAttentionName(client) {
    if (client?.attentionEditedByOperator !== true) return '';
    const value = client.attention;
    return typeof value === 'string' && value.trim() !== '' ? value : '';
}

/**
 * The printed addressee (Prepared For company) of a Stage 5 quote (D7).
 *
 * With the owners addressee in force (the setting on, a strata job), the quote
 * prints the owners addressee unless the operator typed a different one in
 * Stage 5 (client.companyEditedByOperator). A stored company that is itself
 * owners wording ("The Owners - Strata Plan 19180") is kept. Anything else
 * stored (the managing agent's name written by the AI author, or copied from
 * Stage 1) gives way to the owners addressee.
 *
 * Otherwise the stored company prints, then the default (formData.clientCompany,
 * or the business's configured strata addressee), exactly as before. The one
 * exception is a job that is not strata whose stored company is the generated
 * owners addressee the operator never typed: that is the F5 leftover
 * (isGeneratedOwnersAddressee), and the client company prints instead.
 *
 * @param {{ tenantProfile?: object|null, formData?: object|null, storedClient?: object|null, strataPlan?: unknown }} options
 * @returns {string}
 */
export function resolvePrintedAddressee({
    tenantProfile = null,
    formData = null,
    storedClient = null,
    strataPlan = null,
} = {}) {
    const defaultCompany = resolveDefaultClientCompany({ tenantProfile, formData, strataPlan });
    const stored = String(storedClient?.company ?? '').trim();
    if (strataOwnersAddresseeApplies({ tenantProfile, formData, strataPlan })) {
        if (stored && storedClient?.companyEditedByOperator === true) return stored;
        if (stored && isOwnersAddresseeWording(stored, tenantProfile)) return stored;
        return defaultCompany;
    }
    if (stored
        && storedClient?.companyEditedByOperator !== true
        && !isStrataJob({ strataPlan, formData })
        && isGeneratedOwnersAddressee(stored, tenantProfile)) {
        return defaultCompany || '';
    }
    return stored || defaultCompany || '';
}
